import { saveAs } from 'file-saver';
import JSZip from 'jszip';
// Function to remove blank fields from an object
export const removeBlankFields = (data) => {
	const cleanedData = {};
	Object.keys(data).forEach((key) => {
		// Check if the field is not blank or undefined
		if (data[key] !== "" && data[key] !== undefined && data[key] !== null) {
			cleanedData[key] = data[key];
		}
	});
	return cleanedData;
};

export const capitalizeWords = (str) => {
	if (!str) return "";
	return str.toLowerCase().replace(/\b\w/g, function (char) {
		return char.toUpperCase();
	});
}

export const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];


export const getYYYMMDDFormat = (dateStr, sep = '-') => {
	try {
		const date = new Date(dateStr);
		if (!(date instanceof Date) || isNaN(date)) return null;

		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
		const day = String(date.getDate()).padStart(2, '0');

		return `${year}${sep}${month}${sep}${day}`;
	} catch (e) {
		return null
	}
}

export const getDDMMYYYYFormat = (dateStr, sep = '-') => {
	try {
		const date = new Date(dateStr);
		if (!(date instanceof Date) || isNaN(date)) return null;

		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
		const day = String(date.getDate()).padStart(2, '0');

		return `${day}${sep}${month}${sep}${year}`;
	} catch (e) {
		return null
	}
}

export const getHHMMFormat = (dateStr, sep = ':', ampm = true) => {
	try {
		const date = new Date(dateStr);
		if (!(date instanceof Date) || isNaN(date)) return null;

		let hours = date.getHours();
		let minutes = date.getMinutes();

		// Convert hours to 12-hour format if ampm flag is true
		if (ampm) {
			const period = hours >= 12 ? 'PM' : 'AM';
			hours = hours % 12 || 12; // Convert midnight (0) to 12
			hours = String(hours).padStart(2, '0');
			return `${hours}${sep}${String(minutes).padStart(2, '0')} ${period}`;
		} else {
			// Return in 24-hour format
			hours = String(hours).padStart(2, '0');
			return `${hours}${sep}${String(minutes).padStart(2, '0')}`;
		}
	} catch (e) {
		return null
	}
};

export const getDDMonthYYYYFormat = (dateStr, sep = ' ', shortenMonth = false, returnYear = true) => {
	try {
		const date = new Date(dateStr);
		if (!(date instanceof Date) || isNaN(date)) return null;

		// Get the day, month, year
		const day = date.getDate();
		const month = months[date.getMonth()].slice(0, shortenMonth ? 3 : months[date.getMonth()].length);
		const year = date.getFullYear();

		// Format the date as 'Monday, 21 June 2024'
		const formattedDate = returnYear ? `${day}${sep}${month}${sep}${year}` : `${day}${sep}${month}`;

		return formattedDate;

	} catch (e) {
		return null
	}
}

export const getWeekDayFormat = (dateStr, sep = ' ') => {
	try {
		const date = new Date(dateStr);
		if (!(date instanceof Date) || isNaN(date)) return null;


		// Get the day of the week
		const dayOfWeek = weekdays[date.getDay()];

		return dayOfWeek;

	} catch (e) {
		return null
	}
}

export const isHTMLString = (inputString) => {
	if (!inputString) return false;
	// Regular expression to check for HTML tags
	var htmlRegex = /(<([^>]+)>)/ig;
	return htmlRegex.test(inputString);
}

export const convertToHTML = (inputString) => {
	if (!inputString?.trim()) return "";
	if (/^(<p><br><\/p>)+$/.test(inputString.replace(/\s/g, ''))) return "";
	if (isHTMLString(inputString)) return inputString;

	// Split the input string by double line breaks to separate paragraphs
	var paragraphs = inputString.split(/\n\s*\n/);

	// Map each paragraph to wrap it in <p> tags and preserve line breaks
	var htmlString = paragraphs.map(function (paragraph) {
		// Replace single line breaks with <br> tags within each paragraph
		var lines = paragraph.split(/\n/);
		var linesHTML = lines.map(function (line) {
			return line.trim() !== '' ? `${line.trim()}<br>` : ''; // Preserve non-empty lines with <br>
		}).join('');

		return `<p>${linesHTML}</p>`;
	}).join('\n'); // Join paragraphs with a newline

	return htmlString;
}

export const getDateTimeFromTimestamp = (inputString, sep = '_') => {
	const parts = inputString?.split(sep);
	const timestampString = parts ? parts[parts.length - 1].split(".")[0] : '';
	const timestamp = Number(timestampString);

	// Convert the timestamp to a Date object
	const date = new Date(timestamp);

	const formattedDate = getDDMonthYYYYFormat(date);

	return formattedDate;
}

export const getDateTimeFromTimestampOfLinks = (inputString, sep = '-') => {
	const parts = inputString?.split(sep);
	const timestampString = parts ? parts[parts.length - 1].split("/")[0] : '';
	const timestamp = Number(timestampString);

	// Convert the timestamp to a Date object
	const date = new Date(timestamp);

	// Custom date formatting function
	const getDDMonthYYYYFormat = (date) => {
		const day = String(date.getDate()).padStart(2, '0');
		const month = date.toLocaleString('default', { month: 'short' });
		const year = date.getFullYear();
		return `${day} ${month} ${year}`;
	};

	// Format the date
	const formattedDate = getDDMonthYYYYFormat(date);

	return formattedDate;
};

export const getDayDifference = (validUntil) => {
    const today = new Date();
    const untilDate = new Date(validUntil);
    const diffTime = untilDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Round up
    return diffDays;
};

export async function downloadPageWithMedia(companyData) {
	// Helper: fetch blob and return {name, blob}
	async function downloadBlobAsFile(url) {
		const response = await fetch(url);
		const blob = await response.blob();
		return { name: getFileNameFromUrl(url), blob };
	}

	// Helper: fetch URL and convert to base64 string
	async function fetchAsBase64(url) {
		const response = await fetch(url);
		const blob = await response.blob();
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}

	// Check if URL is media file (images, videos, audio, pdf, docs)
	function isMediaFile(url) {
		return /\.(png|jpe?g|gif|svg|webp|bmp|mp4|webm|mp3|wav|ogg|pdf|docx?|xlsx?|pptx?)$/i.test(url);
	}

	// Check if media is video or audio
	function isVideoOrAudio(url) {
		return /\.(mp4|webm|mp3|wav|ogg)$/i.test(url);
	}
	// Extract filename from URL
	function getFileNameFromUrl(url) {
		const parts = url.split('/');
		return parts[parts.length - 1].split('?')[0].split('#')[0];
	}

	// Start zipping
	const zip = new JSZip();
	const mediaFolder = zip.folder("media");

	// Clone whole document element
	const doc = document.documentElement.cloneNode(true);
	const hideElementById = (id) => {
		const el = doc.querySelector(`#${id}`);
		if (el) {
			el.style.display = "none";
		}
	};

	const hideElementsByClass = (className) => {
		const elements = doc.querySelectorAll(`.${className}`);
		elements.forEach(el => {
			el.style.display = "none";
		});
	};

	["drawer-sidebar", "offline-download", "expand-collapse", 'floating-icons', 'add-scheudle-icon'].forEach(hideElementById);

	["left-fixed-container"].forEach(hideElementsByClass);
	const iconButtons = doc.querySelectorAll('.MuiIconButton-root');
	iconButtons.forEach(btn => {
		btn.style.display = "none";
	});
	// Remove all stylesheet links (we'll inline CSS)
	doc.querySelectorAll('link[rel="stylesheet"]').forEach(el => el.remove());

	// Collect CSS text from stylesheets and append as <style> in cloned doc
	let cssText = "";
	for (const sheet of document.styleSheets) {
		try {
			if (!sheet.cssRules) continue;
			for (const rule of sheet.cssRules) {
				cssText += rule.cssText + "\n";
			}
		} catch (e) { /* Ignore CORS protected sheets */ }
	}
	const styleEl = document.createElement("style");
	styleEl.textContent = cssText;
	doc.querySelector("head").appendChild(styleEl);

	// Process media elements: img, video, audio, embed, object, source, iframe
	async function processMediaElements(selector, attr) {
		const elements = doc.querySelectorAll(selector);
		for (const el of elements) {
			let url = el.getAttribute(attr);
			if (!url || url.startsWith("data:")) continue;

			if (url.includes("view.officeapps.live.com")) {
				const srcParamMatch = url.match(/[?&]src=([^&]+)/);
				if (srcParamMatch) {
					const realDocUrl = decodeURIComponent(srcParamMatch[1]);
					try {
						const { name, blob } = await downloadBlobAsFile(realDocUrl);
						mediaFolder.file(name, blob);
						const link = document.createElement("a");
						link.href = `media/${name}`;
						link.textContent = `Open ${name}`;
						link.target = "_blank";
						link.style.display = "block";
						link.style.marginTop = "1.5rem"; // You can use "1rem" or "2rem" as needed
						link.style.textAlign = "left";
						el.replaceWith(link);
					} catch (e) {
						console.warn("Failed to download Office doc:", realDocUrl, e);
					}
					continue;
				}
			}
			if (url.includes("pdfjs/web/viewer.html") && url.includes("file=")) {
				const fileParamMatch = url.match(/[?&]file=([^&]+)/);
				if (fileParamMatch) {
					const realPdfUrl = decodeURIComponent(fileParamMatch[1]);
					try {
						const { name, blob } = await downloadBlobAsFile(realPdfUrl);
						mediaFolder.file(name, blob);
						replaceWithPdfIframe(el, name);
					} catch (e) {
						console.warn("Failed to download PDF file:", realPdfUrl, e);
					}
					continue;
				}
			}

			if (isMediaFile(url)) {
				try {
					if (isVideoOrAudio(url)) {
						// For videos/audio - download file and link locally
						const { name, blob } = await downloadBlobAsFile(url);
						mediaFolder.file(name, blob);
						el.setAttribute(attr, `media/${name}`);
					} else {
						// For images etc - inline base64
						const base64Data = await fetchAsBase64(url);
						el.setAttribute(attr, base64Data);
					}
				} catch (e) {
					console.warn("Failed to fetch media:", url, e);
				}
			}
		}
	}
	async function replaceWithPdfIframe(el, filename) {

		const iframe = document.createElement("iframe");
		iframe.src = `media/${filename}`;
		iframe.width = "100%";
		iframe.height = "600px";
		iframe.style.border = "none";
		el.replaceWith(iframe);
	}

	await processMediaElements("img[src]", "src");
	await processMediaElements("video[src]", "src");
	await processMediaElements("audio[src]", "src");
	await processMediaElements("embed[src]", "src");
	await processMediaElements("object[data]", "data");
	await processMediaElements("source[src]", "src");
	await processMediaElements("iframe[src]", "src");

	// Inline all external scripts
	const scripts = doc.querySelectorAll("script[src]");
	for (const script of scripts) {
		try {
			const resp = await fetch(script.src);
			const jsText = await resp.text();
			const inlineScript = document.createElement("script");
			inlineScript.textContent = jsText;
			script.parentNode.replaceChild(inlineScript, script);
		} catch {
			console.warn("Failed to inline script", script.src);
		}
	}
	// Serialize cloned doc back to string
	const serializer = new XMLSerializer();
	const htmlString = serializer.serializeToString(doc);

	// Add HTML file to zip
	zip.file("index.html", htmlString);
	const now = new Date();
	const timestamp = now.toISOString().replace(/[-:]/g, "").slice(0, 15).replace("T", "_");

	// Generate ZIP and trigger download
	const content = await zip.generateAsync({ type: "blob" });
	saveAs(content, `${companyData?.company_name}_${timestamp}.zip`);
}

export const getCountryColor = (countryName) => {
  if (!countryName) return "#333333"; // fallback color
 
  // Convert string to hash
  let hash = 0;
  for (let i = 0; i < countryName.length; i++) {
    hash = countryName.charCodeAt(i) + ((hash << 5) - hash);
  }
 
  // Generate RGB components based on hash
  const r = (hash >> 0) & 0xff;
  const g = (hash >> 8) & 0xff;
  const b = (hash >> 16) & 0xff;
 
  // Ensure darker colors by limiting the range to 30-180 (avoiding light colors)
  const darken = (v) => Math.min(180, Math.max(30, v));
 
  // Apply darkness correction
  const R = darken(r);
  const G = darken(g);
  const B = darken(b);
 
  // Convert to HEX string
  const hex = `#${((1 << 24) + (R << 16) + (G << 8) + B)
    .toString(16)
    .slice(1)}`;

  return hex;
}

// Fixed brand color per offering (chips use white text, so these are dark enough
// for contrast). Keyed on the lowercased offering name. Anything unmapped falls
// back to the deterministic country-style hash so future offerings still get a color.
const OFFERING_COLOR_MAP = {
  'des': '#1565C0',        // blue
  'af-x': '#6A1B9A',       // purple
  'amps': '#2E7D32',       // green
  'gwx-a': '#C62828',      // red
  'dilx saas': '#00695C',  // teal
};

export const getOfferingColor = (offeringName) => {
  if (!offeringName) return '#9e9e9e';
  const key = offeringName.trim().toLowerCase();
  return OFFERING_COLOR_MAP[key] || getCountryColor(offeringName);
};

// Parse a #rrggbb hex into [r, g, b]. Assumes a valid 6-digit hex (which all
// getOfferingColor outputs are).
const hexToRgb = (hex) => {
  const int = parseInt(hex.slice(1), 16);
  return [(int >> 16) & 0xff, (int >> 8) & 0xff, int & 0xff];
};

// Soft chip style derived from the offering's base brand color: a pale ~12% tint
// background with the dark base hue as text and a hairline border. Unmapped/empty
// names fall back to a muted grey. Additive helper for the dashboard only — the
// shared getOfferingColor values are untouched so other screens stay identical.
export const getOfferingChipStyle = (name) => {
  if (!name) {
    return {
      bgcolor: 'rgba(158,158,158,0.15)',
      color: '#616161',
      border: '1px solid rgba(158,158,158,0.30)',
    };
  }
  const base = getOfferingColor(name);
  const [r, g, b] = hexToRgb(base);
  return {
    bgcolor: `rgba(${r},${g},${b},0.12)`,
    color: base,
    border: `1px solid rgba(${r},${g},${b},0.24)`,
  };
};

// Softened accent color (~50% alpha) for the card's left accent bar, so the card
// reads lighter while still giving an offering colour cue.
export const getOfferingAccentColor = (name) => {
  if (!name) return 'rgba(158,158,158,0.5)';
  const [r, g, b] = hexToRgb(getOfferingColor(name));
  return `rgba(${r},${g},${b},0.5)`;
};
